import React, { useEffect, useRef, useMemo } from 'react';

/**
 * Local development stub for @user-interfaces/evo-app-container.
 *
 * In production the Evo shell provides this module via its import map.
 * During local development this stub is resolved instead (see the Vite
 * resolve alias in vite.config.ts).
 *
 * The stub renders the underlying oc-component directly by building the
 * OC registry URL from the componentParams, replicating the behaviour of
 * the real EvoMfeApp so the widget is visible during local development.
 */

export interface EvoMfeAppProps {
  componentName: string;
  componentVersion: string;
  componentParams?: Record<string, unknown>;
  className?: string;
}

const OC_CLIENT_URL = 'https://mfe.workspace.accessacloud.com/oc-client/client.js';

function ensureOcClient(): void {
  if (document.querySelector(`script[src="${OC_CLIENT_URL}"]`)) return;
  const script = document.createElement('script');
  script.src = OC_CLIENT_URL;
  script.async = true;
  document.head.appendChild(script);
}

function buildHref(
  componentName: string,
  componentVersion: string,
  componentParams?: Record<string, unknown>,
): string {
  const params = new URLSearchParams();
  if (componentParams) {
    for (const [key, value] of Object.entries(componentParams)) {
      if (value !== undefined && value !== null) {
        params.set(key, String(value));
      }
    }
  }
  const baseUrl = `https://mfe.workspace.accessacloud.com/registry/${componentName}/${componentVersion}/`;
  return params.toString() ? `${baseUrl}?${params.toString()}` : baseUrl;
}

export const EvoMfeApp: React.FC<EvoMfeAppProps> = ({
  componentName,
  componentVersion,
  componentParams,
  className,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);

  const serialisedParams = useMemo(
    () => JSON.stringify(componentParams ?? {}),
    [componentParams],
  );

  useEffect(() => {
    ensureOcClient();
  }, []);

  useEffect(() => {
    if (!containerRef.current) return;

    const parsedParams = JSON.parse(serialisedParams) as Record<string, unknown>;
    const href = buildHref(componentName, componentVersion, parsedParams);

    const existing = containerRef.current.querySelector('oc-component');
    if (existing && existing.getAttribute('href') === href) return;

    containerRef.current.innerHTML = '';
    const oc = document.createElement('oc-component');
    oc.setAttribute('href', href);
    containerRef.current.appendChild(oc);

    if (typeof (window as unknown as Record<string, unknown>).oc !== 'undefined') {
      (window as unknown as Record<string, { renderUnloadedComponents: () => void }>).oc.renderUnloadedComponents();
    }
  }, [componentName, componentVersion, serialisedParams]);

  return (
    <div
      ref={containerRef}
      data-evo-mfe-app
      data-component-name={componentName}
      data-component-version={componentVersion}
      className={className}
    />
  );
};
